import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Trash2, AlertTriangle, MapPin, ChevronDown, X } from "lucide-react";
import { money, pct } from "./helpers";

const PRICING_METHODS = [
  "unit", "area", "linear", "volume", "weight", "base_plus_area", "labor_material", "flat",
];

// A calculator is measurable via the map when its input unit is area or length.
const MEASURABLE_UNITS = new Set(["sqft", "linear_ft"]);

export default function LineRow({
  line, priced, calculators, belowTarget, marginTarget, onChange, onRemove, idx,
  savedMeasurements = [], onOpenMeasureDialog, onPickSavedMeasurement, onClearMeasurement,
}) {
  const [expanded, setExpanded] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const p = priced || {};
  const calc = calculators.find((c) => c.key === line.calculator_key);
  const isMeasurable = calc && MEASURABLE_UNITS.has(calc.input_unit);
  const linkedMeasurement = line.measurement_id
    ? savedMeasurements.find((m) => m.id === line.measurement_id)
    : null;
  const compatibleSaved = savedMeasurements.filter((m) => {
    // Same input unit as this line's calculator when possible; otherwise show all.
    if (!calc) return true;
    const mc = calculators.find((c) => c.key === m.calculator_key);
    return !mc || mc.input_unit === calc.input_unit;
  });
  return (
    <div
      className={`border rounded-lg p-3 ${belowTarget ? "border-amber-300 bg-amber-50/40" : "border-zinc-200"}`}
      data-testid={`line-row-${idx}`}
    >
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <Input
            value={line.service_name || ""}
            onChange={(e) => onChange({ service_name: e.target.value })}
            placeholder="Line name (e.g. Front lawn mow)"
            className="mb-2 font-medium"
            data-testid={`line-name-${idx}`}
          />
          <div className="grid grid-cols-4 gap-2">
            <Field label="Calculator">
              <Select value={line.calculator_key || "custom"} onValueChange={(v) => onChange({ calculator_key: v })}>
                <SelectTrigger className="h-9 text-xs" data-testid={`line-calc-${idx}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {calculators.map((c) => (
                    <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label={calc?.input_unit === "linear_ft" ? "Length (ft)" : (calc?.input_unit === "sqft" ? "Area (ft²)" : "Quantity")}>
              <div className="flex items-center gap-1">
                <Input
                  type="number" step="0.01"
                  value={line.quantity ?? ""}
                  onChange={(e) => onChange({ quantity: e.target.value })}
                  className="h-9 text-sm font-mono flex-1"
                  data-testid={`line-qty-${idx}`}
                />
                {isMeasurable && (
                  <>
                    <button
                      type="button"
                      onClick={() => onOpenMeasureDialog?.(idx)}
                      className={`h-9 w-9 shrink-0 flex items-center justify-center rounded border ${linkedMeasurement ? "bg-orange-50 border-[#FF5A00] text-[#FF5A00]" : "border-zinc-300 text-zinc-600 hover:border-[#FF5A00] hover:text-[#FF5A00]"}`}
                      title={linkedMeasurement ? "Edit map measurement" : "Measure on map"}
                      data-testid={`line-map-btn-${idx}`}
                    >
                      <MapPin className="w-4 h-4" />
                    </button>
                    <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="h-9 w-6 shrink-0 flex items-center justify-center rounded border border-zinc-300 text-zinc-600 hover:border-[#FF5A00] hover:text-[#FF5A00]"
                          title="Pick a saved measurement"
                          data-testid={`line-saved-btn-${idx}`}
                        >
                          <ChevronDown className="w-3.5 h-3.5" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-72 p-2" align="end" data-testid={`line-saved-popover-${idx}`}>
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
                          Saved measurements for this property
                        </div>
                        {compatibleSaved.length === 0 && (
                          <div className="text-xs text-zinc-500 p-2">
                            No compatible saved measurements yet.
                          </div>
                        )}
                        <div className="max-h-56 overflow-y-auto">
                          {compatibleSaved.map((m) => (
                            <button
                              key={m.id}
                              type="button"
                              onClick={() => { onPickSavedMeasurement?.(idx, m); setPickerOpen(false); }}
                              className={`w-full text-left px-2 py-1.5 rounded hover:bg-zinc-100 flex items-center justify-between gap-2 ${line.measurement_id === m.id ? "bg-orange-50" : ""}`}
                              data-testid={`line-saved-item-${idx}-${m.id}`}
                            >
                              <div className="min-w-0">
                                <div className="text-sm truncate">{m.label || m.calculator_key}</div>
                                <div className="text-[10px] text-zinc-500 font-mono-accent">
                                  {Number(m.output_value || 0).toLocaleString()} {m.output_unit || m.input_unit}
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      </PopoverContent>
                    </Popover>
                  </>
                )}
              </div>
            </Field>
            <Field label="Method">
              <Select value={line.pricing_method || "unit"} onValueChange={(v) => onChange({ pricing_method: v })}>
                <SelectTrigger className="h-9 text-xs" data-testid={`line-method-${idx}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRICING_METHODS.map((m) => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="$ / unit">
              <Input
                type="number" step="0.01"
                value={line.unit_price ?? ""}
                onChange={(e) => onChange({ unit_price: e.target.value })}
                className="h-9 text-sm font-mono"
                data-testid={`line-unitprice-${idx}`}
              />
            </Field>
          </div>

          {linkedMeasurement && (
            <div className="mt-2 flex items-center gap-2 text-[11px] text-[#FF5A00]" data-testid={`line-measurement-badge-${idx}`}>
              <MapPin className="w-3 h-3" />
              <span>Linked to <strong>{linkedMeasurement.label || "measurement"}</strong> ({Number(linkedMeasurement.output_value || 0).toLocaleString()} {linkedMeasurement.output_unit || linkedMeasurement.input_unit})</span>
              <button
                type="button"
                onClick={() => onClearMeasurement?.(idx)}
                className="text-zinc-400 hover:text-red-600"
                title="Unlink measurement (keeps current quantity)"
                data-testid={`line-measurement-unlink-${idx}`}
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          )}

          {expanded && (
            <div className="mt-3 grid grid-cols-4 gap-2">
              <Field label="Min charge">
                <Input type="number" step="0.01" value={line.min_charge ?? ""} onChange={(e) => onChange({ min_charge: e.target.value })} className="h-9 text-sm font-mono" data-testid={`line-min-${idx}`} />
              </Field>
              <Field label="Material $ / unit">
                <Input type="number" step="0.01" value={line.material_cost_per_unit ?? ""} onChange={(e) => onChange({ material_cost_per_unit: e.target.value })} className="h-9 text-sm font-mono" />
              </Field>
              <Field label="Markup %">
                <Input type="number" step="0.1" value={line.material_markup_pct ?? ""} onChange={(e) => onChange({ material_markup_pct: e.target.value })} className="h-9 text-sm font-mono" />
              </Field>
              <Field label="Labor hrs">
                <Input type="number" step="0.1" value={line.labor_hours ?? ""} onChange={(e) => onChange({ labor_hours: e.target.value })} className="h-9 text-sm font-mono" />
              </Field>
              <Field label="Labor $/hr">
                <Input type="number" step="0.01" value={line.labor_rate_per_hour ?? ""} onChange={(e) => onChange({ labor_rate_per_hour: e.target.value })} className="h-9 text-sm font-mono" />
              </Field>
              <div className="col-span-2">
                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Override customer price</Label>
                <Input type="number" step="0.01" placeholder="Overrides everything above"
                  value={line.price_override ?? ""}
                  onChange={(e) => onChange({ price_override: e.target.value })}
                  className="h-9 text-sm font-mono" data-testid={`line-override-${idx}`} />
              </div>
            </div>
          )}

          <button
            onClick={() => setExpanded((v) => !v)}
            className="mt-2 text-[11px] text-zinc-500 hover:text-zinc-900"
            data-testid={`line-expand-${idx}`}
          >
            {expanded ? "Hide details" : "More options (material, labor, min charge, override)"}
          </button>
        </div>

        <div className="w-40 shrink-0 text-right">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Customer price</div>
          <div className="font-heading text-xl font-bold text-zinc-900" data-testid={`line-price-${idx}`}>
            {money(p.customer_price)}
          </div>
          <div className="text-[10px] text-zinc-500">
            cost {money(p.internal_cost)} · {pct(p.margin_pct)}
          </div>
          {p.applied_min_charge && (<div className="text-[10px] mt-1 text-amber-700">Min charge applied</div>)}
          {p.price_override_used && (<div className="text-[10px] mt-1 text-indigo-700">Manual price</div>)}
          {belowTarget && (
            <div className="text-[10px] mt-1 flex items-center justify-end gap-1 text-amber-700">
              <AlertTriangle className="w-3 h-3" /> Below {marginTarget}%
            </div>
          )}
          <button
            onClick={onRemove}
            className="mt-2 text-zinc-400 hover:text-red-600"
            data-testid={`line-remove-${idx}`}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</Label>
      {children}
    </div>
  );
}
